import { useAppSelector } from "redux/hooks";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { LEVEL_TYPE } from "api/interfaces/commonInterface.interface";
import { UserGroupUserPermission } from "api/interfaces/userInterface.interface";
import { useMemo } from "react";
import useApiError from "./useApiError";
import { checkUserPermission } from "pages/users/userGroup/add/PermissionPolicy";

interface Props {}

export function useDwPermission(props: Props) {
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const { lang, intlString } = useApiError();

  const getCheckList = (items: string[]) => {
    return items.map((item: string) => {
      if (item) {
        return {
          name: item === "videoShare" ? "view" : item,
          label: intlString(
            lang.user.permission[item as keyof typeof lang.user.permission]
          ),
        };
      }
      return null;
    });
  };

  const alertPermission = {
    title: "Alert",
    permissionName: "alert",
    check: getCheckList(["manageAlertRules", "viewAlerts"]),
  };
  const userPermission = {
    title: "User Management",
    permissionName: "user",
    check: getCheckList(["manageUsersAndGroups", "viewUsersAndGroups"]),
  };
  const reportPermission = {
    title: "Report",
    permissionName: "report",
    check: getCheckList(["manageReportRules", "viewReports"]),
  };

  const recorderPermission = {
    title: "Recorders",
    permissionName: "recorders",
    check: getCheckList(["manageRecordersAndGroups", "viewRecordersAndGroups"]),
  };
  const orgPermission = {
    title: "Organization Management",
    permissionName: "organization",
    check: getCheckList(["manageAccount", "viewAccount"]),
  };

  const videoPermission = {
    title: "Video Share",
    permissionName: "videoShare",
    check: getCheckList(["videoShare"]),
  };

  const billingPermission = {
    title: "Subscription",
    permissionName: "billing",
    check: getCheckList(["manageBillingAndLicenses"]),
  };

  const dwBillingPermission = {
    title: "Subscription",
    permissionName: "dwBilling",
    check: [
      {
        name: "subscriptionOrganization",
        label: "Organization",
        items: [
          { name: "suspend", label: "Suspend" },
          { name: "lowLevelAccess", label: "Low-Level Access" },
        ],
      },

      {
        name: "subscriptionKeys",
        label: "Subscription Keys",
        items: [{ name: "generateKeys", label: "Generate" }],
      },
      {
        name: "serviceAndPricing",
        label: "Services & Pricing",
        items: [{ name: "serviceAndPricingUpdate", label: "Update" }],
      },
      {
        name: "cumulus",
        label: "Cumulus",
        items: [{ name: "generateCumulus", label: "Generate" }],
      },
    ],
  };

  const settingPermission = {
    title: "Management",
    permissionName: "settings",
    check: getCheckList([
      "videoGuideSetting",
      "techSupport",
      "siteNotice",
      "emails",
      "logging",
      "announcement",
      "releaseNotes",
    ]),
  };

  const maintenancePermission = {
    title: "Maintenance",
    permissionName: "maintenance",
    check: getCheckList(["manageMaintenance"]),
  };

  const euSubscriptionPermission = {
    title: "Subscription",
    permissionName: "euSubscription",
    check: getCheckList(["manageSubscription", "viewSubscription"]),
  };

  const accountLevelPermissionMap: Record<LEVEL_TYPE, any[]> = {
    DW: [
      orgPermission,
      userPermission,
      dwBillingPermission,
      settingPermission,
      maintenancePermission,
    ],
    EU: [
      userPermission,
      alertPermission,
      recorderPermission,
      reportPermission,
      videoPermission,
      euSubscriptionPermission,
      maintenancePermission,
    ],
    SP: [
      userPermission,
      alertPermission,
      orgPermission,
      reportPermission,
      maintenancePermission,
    ],
    CP: [
      userPermission,
      alertPermission,
      orgPermission,
      reportPermission,
      billingPermission,
      maintenancePermission,
    ],
    "": [
      userPermission,
      alertPermission,
      orgPermission,
      reportPermission,
      billingPermission,
      maintenancePermission,
    ],
  };

  const accountLevelPermissionNames = accountLevelPermissionMap[
    selectedAccount.accountLevelExtendSubCp
  ]?.flatMap((permission) =>
    permission.check.flatMap((c: any) => [
      c.name,
      ...(c.items || []).map((item: any) => item.name),
    ])
  );

  const accountLevelRenderingArray: Record<LEVEL_TYPE, any[]> = {
    DW: [
      [orgPermission, userPermission],
      [dwBillingPermission, settingPermission],
      [maintenancePermission],
    ],
    EU: [
      [userPermission, alertPermission],
      [recorderPermission, reportPermission],
      [videoPermission, euSubscriptionPermission],
      [maintenancePermission],
    ],
    SP: [
      [userPermission, alertPermission],
      [orgPermission, reportPermission],
      [maintenancePermission],
    ],
    CP: [
      [userPermission, alertPermission],
      [orgPermission, reportPermission],
      [billingPermission, maintenancePermission],
    ],
    "": [
      [userPermission, alertPermission],
      [orgPermission, reportPermission],
      [billingPermission, maintenancePermission],
    ],
  };

  const invertPermissionBooleans = (
    permission: UserGroupUserPermission
  ): UserGroupUserPermission => {
    return Object.fromEntries(
      Object.entries(permission).map(([key, value]) => {
        if (
          accountLevelPermissionNames.includes(key) &&
          !(
            selectedAccount.accountLevelExtendSubCp === LEVEL_TYPE.EU &&
            key === "viewRecordersAndGroups"
          ) &&
          !(
            selectedAccount.accountLevelExtendSubCp !== LEVEL_TYPE.EU &&
            key === "viewAccount"
          )
        ) {
          return [key, typeof value === "boolean" ? !value : value];
        }
        return [key, value];
      })
    ) as UserGroupUserPermission;
  };

  const defaultDisabledUserPermission: UserGroupUserPermission = useMemo(() => {
    const initialDisable = {
      manageAlertRules: false,
      viewAlerts: false,
      manageReportRules: false,
      viewReports: false,
      manageRecordersAndGroups: false,
      viewRecordersAndGroups: true,
      manageAccount: false,
      viewAccount:
        selectedAccount.accountLevel !== LEVEL_TYPE.EU ? true : false,
      view: false,
      manageBillingAndLicenses: false,
      groupName: "",
      enableDelete: false,
      subscriptionOrganization: false,
      suspend: false,
      lowLevelAccess: false,
      subscriptionKeys: false,
      generateKeys: false,
      serviceAndPricing: false,
      serviceAndPricingUpdate: false,
      cumulus: false,
      generateCumulus: false,
      myOrganization: false,
      videoGuideSetting: false,
      techSupport: false,
      siteNotice: false,
      emails: false,
      viewSubscription: false,
      manageSubscription: false,
      announcement: false,
      logging: false,
      releaseNotes: false,
      manageMaintenance: false,
    };
    // admin group 은 권한 모두 가능
    if (selectedAccount.isAdminGroup) {
      return {
        ...initialDisable,
        ...checkUserPermission(selectedAccount.isAdminGroup).disable,
      };
    }

    // admin group 아닌 수정 가능한 사람은 내가 갖고 있는 권한만 가능하도록
    return {
      ...invertPermissionBooleans(selectedAccount.userPermission),
      ...checkUserPermission(selectedAccount.isAdminGroup).disable,
    };
  }, [
    selectedAccount.userPermission,
    selectedAccount.isAdminGroup,
    selectedAccount.accountLevel,
  ]);

  function normalizeUserGroupUserPermission(
    userPermission: UserGroupUserPermission
  ): UserGroupUserPermission {
    const allKeys = Object.keys(
      userPermission as UserGroupUserPermission
    ) as (keyof UserGroupUserPermission)[];

    const result: UserGroupUserPermission = allKeys.reduce((acc, key) => {
      if (key === "groupName") {
        acc[key] = userPermission[key] ?? "";
      } else {
        if (accountLevelPermissionNames.includes(key)) {
          acc[key] = userPermission[key] ?? false;
        } else {
          acc[key] = false as any;
        }
      }
      return acc;
    }, {} as UserGroupUserPermission);

    return {
      ...result,
      enableAllPermissions: isAllCheckForEnableAllPermission(result),
    };
  }

  function isAllCheckForEnableAllPermission(
    permission: UserGroupUserPermission
  ): boolean {
    return accountLevelPermissionNames.every(
      (key) => permission[key as keyof UserGroupUserPermission]
    );
  }

  return {
    accountLevelPermissionNames,
    accountLevelRenderingArray,
    invertPermissionBooleans,
    defaultDisabledUserPermission,
    normalizeUserGroupUserPermission,
    isAllCheckForEnableAllPermission,
  };
}
